import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PartInfo } from '../models/partinfo';
import { Proposal } from '../models/proposal';
import { CreateProposalDTO } from '../models/createproposaldto';
import { PartBasicInfo } from '../models/partbasicinfo';
import { PackageLevelTemplate } from '../models/packageleveltemplate';
import { PackageMaterial } from '../models/packagematerial';
import { UnitOfMeasurement } from '../models/internal/unit-of-measurement';
import { ProposalEstimate } from '../models/proposal-estimate';
import { ProposalLocation } from '../models/internal/proposal-location';

@Injectable()
export class ProposalService {
    private proposalToReviewIdSource = new BehaviorSubject<number>(null);
    private globalUOMOnLoadSource = new BehaviorSubject<UnitOfMeasurement>(null);
    private globalUOMOnUserToggleSource = new BehaviorSubject<UnitOfMeasurement>(null);
    private globalUOMOnSubmitSource = new BehaviorSubject<UnitOfMeasurement>(null);
    private savedproposalIdsSource = new BehaviorSubject<number[]>(null);
    private partWeightInProposalSource = new BehaviorSubject<number>(null);
    private proposalToViewIdSource = new BehaviorSubject<number>(null);
    private proposalSource = new BehaviorSubject<CreateProposalDTO>(null);
    private partInfoIdsSource = new BehaviorSubject([]);
    private partInfoListSource = new BehaviorSubject([]);
    private partInfoToBeCreatedListSource = new BehaviorSubject([]);
    private packagelevelTemplateListSource = new BehaviorSubject([]);
    private packageTypeIdSource = new BehaviorSubject<number>(null);
    private packageTypeDescriptionSource = new BehaviorSubject<string>(null);
    private proposalManufacturingLocationSource = new BehaviorSubject<ProposalLocation>(null);
    private proposalSecondaryProcessingLocationSource = new BehaviorSubject<ProposalLocation>(null);
    private transportModeIdSource = new BehaviorSubject<number>(null);
    private transportModeDescriptionSource = new BehaviorSubject<string>(null);
    private sourceTypeDescriptionSource = new BehaviorSubject<string>(null);
    private sourceTypeIdSource = new BehaviorSubject<number>(null);
    private packagingMaterialIdSource = new BehaviorSubject(null);
    private packagingPrimaryTotalWeightSource = new BehaviorSubject<number>(null);
    private packagingMaterialPrimaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private packagingMaterialSecondaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private packagingMaterialTertiaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private packagingTemplatePrimaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private packagingTemplateSecondaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private packagingTemplateTertiaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private packagingDunnageTypePrimaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private packagingDunnageTypeSecondaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private packagingDunnageTypeTertiaryDescriptionSource = new BehaviorSubject<
        string
    >('');
    private proposalTabsSelectedIndexSource = new BehaviorSubject<number>(0);
    private highestTabIndexSource = new BehaviorSubject<number>(0);
    private createProposalDTOSource = new BehaviorSubject<CreateProposalDTO>(
        null
    );
    private proposalEstimateSource = new BehaviorSubject<ProposalEstimate[]>(
        null
    );
    private dunnageTypeMasterListSource = new BehaviorSubject([]);
    private packageTypeMasterListSource = new BehaviorSubject([]);
    private reasonsNonSubTestDocMasterListSource = new BehaviorSubject([]);
    private transportModeMasterListSource = new BehaviorSubject([]);
    private sourceTypeMasterListSource = new BehaviorSubject([]);
    private InnerPacksourceTypeMasterListSource = new BehaviorSubject([]);
    private packageMaterialMasterListSource = new BehaviorSubject([]);
    proposalToReviewId  = this.proposalToReviewIdSource.asObservable();
    globalUOMOnLoad  = this.globalUOMOnLoadSource.asObservable();
    globalUOMOnUserToggle  = this.globalUOMOnUserToggleSource.asObservable();
    globalUOMOnSubmit  = this.globalUOMOnSubmitSource.asObservable();
    savedproposalIds  = this.savedproposalIdsSource.asObservable();
    partWeightInProposal  = this.partWeightInProposalSource.asObservable();
    proposalToViewId  = this.proposalToViewIdSource.asObservable();
    proposal = this.proposalSource.asObservable();
    partInfoIds = this.partInfoIdsSource.asObservable();
    partInfoList = this.partInfoListSource.asObservable();
    partInfoToBeCreatedList = this.partInfoToBeCreatedListSource.asObservable();
    packagelevelTemplateList = this.packagelevelTemplateListSource.asObservable();
    packageTypeId = this.packageTypeIdSource.asObservable();
    packageTypeDescription = this.packageTypeDescriptionSource.asObservable();
    proposalManufacturingLocation = this.proposalManufacturingLocationSource.asObservable();
    proposalSecondaryProcessingLocation = this.proposalSecondaryProcessingLocationSource.asObservable();
    transportModeId = this.transportModeIdSource.asObservable();
    transportModeDescription = this.transportModeDescriptionSource.asObservable();
    sourceTypeDescription = this.sourceTypeDescriptionSource.asObservable();
    sourceTypeId = this.sourceTypeIdSource.asObservable();
    packagingMaterialId = this.packagingMaterialIdSource.asObservable();
    packagingPrimaryTotalWeight = this.packagingPrimaryTotalWeightSource.asObservable();
    packagingMaterialPrimaryDescription = this.packagingMaterialPrimaryDescriptionSource.asObservable();
    packagingMaterialSecondaryDescription = this.packagingMaterialSecondaryDescriptionSource.asObservable();
    packagingMaterialTertiaryDescription = this.packagingMaterialTertiaryDescriptionSource.asObservable();
    packagingTemplatePrimaryDescription = this.packagingTemplatePrimaryDescriptionSource.asObservable();
    packagingTemplateSecondaryDescription = this.packagingTemplateSecondaryDescriptionSource.asObservable();
    packagingTemplateTertiaryDescription = this.packagingTemplateTertiaryDescriptionSource.asObservable();
    packagingDunnageTypePrimaryDescription = this.packagingDunnageTypePrimaryDescriptionSource.asObservable();
    packagingDunnageTypeSecondaryDescription = this.packagingDunnageTypeSecondaryDescriptionSource.asObservable();
    packagingDunnageTypeTertiaryDescription = this.packagingDunnageTypeTertiaryDescriptionSource.asObservable();
    proposalTabsSelectedIndex = this.proposalTabsSelectedIndexSource.asObservable();
    highestTabIndex = this.highestTabIndexSource.asObservable();
    createProposalDTO = this.createProposalDTOSource.asObservable();
    proposalEstimate = this.proposalEstimateSource.asObservable();
    dunnageTypeMasterList = this.dunnageTypeMasterListSource.asObservable();
    packageTypeMasterList = this.packageTypeMasterListSource.asObservable();
    reasonsNonSubTestDocMasterList = this.reasonsNonSubTestDocMasterListSource.asObservable();
    transportModeMasterList = this.transportModeMasterListSource.asObservable();
    sourceTypeMasterList = this.sourceTypeMasterListSource.asObservable();
    innerPackSourcetypeMasterList = this.InnerPacksourceTypeMasterListSource.asObservable();
    packageMaterialMasterList = this.packageMaterialMasterListSource.asObservable();

    constructor() {}

    RefreshProposalSummaryFields() {
        this.packagingMaterialPrimaryDescriptionSource.next(null);
        this.packagingTemplatePrimaryDescriptionSource.next(null);
        this.packagingMaterialSecondaryDescriptionSource.next(null);
        this.packagingTemplateSecondaryDescriptionSource.next(null);
        this.packagingDunnageTypeSecondaryDescriptionSource.next(null);
        this.packagingDunnageTypeSecondaryDescriptionSource.next(null);
    }

    RefreshproposalToReviewId(id: number) {
        this.proposalToReviewIdSource.next(id);
    }

    RefreshsavedproposalIds(ids: number[]) {
        this.savedproposalIdsSource.next(ids);
    }

    RefreshglobalUOMOnLoad(uom: UnitOfMeasurement) {
        this.globalUOMOnLoadSource.next(uom);
    }
    RefreshglobalUOMOnUserToggle(uom: UnitOfMeasurement) {
        this.globalUOMOnUserToggleSource.next(uom);
    }
    RefreshglobalUOMOnSubmit(uom: UnitOfMeasurement) {
        this.globalUOMOnSubmitSource.next(uom);
    }
    RefreshpackagingPrimaryTotalWeight(totalWeight: number) {
        this.packagingPrimaryTotalWeightSource.next(totalWeight);
    }
    RefreshpartWeightInProposal(weight: number) {
        this.partWeightInProposalSource.next(weight);
    }
    RefreshproposalToViewId(id: number) {
        this.proposalToViewIdSource.next(id);
    }
    RefreshdunnageTypeMasterList(dunnageTypes: []) {
        this.dunnageTypeMasterListSource.next(dunnageTypes);
    }
    RefreshpackageTypeMasterList(packageTypes: []) {
        this.packageTypeMasterListSource.next(packageTypes);
    }
    RefreshreasonsNonSubTestDocMasterList(reasonsNonSubTestDoc: []) {
        this.reasonsNonSubTestDocMasterListSource.next(reasonsNonSubTestDoc);
    }

    RefreshtransportModeMasterList(transportModes: []) {
        this.transportModeMasterListSource.next(transportModes);
    }
    RefreshsourceTypeMasterList(sourceTypes: []) {
        this.sourceTypeMasterListSource.next(sourceTypes);
    }
    RefreshInnerPacksourceTypeMasterList(innerPackSourceTypes: []) {
        this.InnerPacksourceTypeMasterListSource.next(innerPackSourceTypes);
    }
    RefreshpackageMaterialMasterList(packageMaterials: PackageMaterial[]) {
        this.packageMaterialMasterListSource.next(packageMaterials);
    }

    RefreshCreateProposalDTO(prop: CreateProposalDTO) {
        // this.RefreshPartInfoList(prop.DefaultProposal.PartInfo.Parts);
        this.createProposalDTOSource.next(prop);
    }
    RefreshProposalEstimate(prop: ProposalEstimate[]) {
        this.proposalEstimateSource.next(prop);
    }
    RefreshProposal(createProposalDTO: CreateProposalDTO) {
        this.proposalSource.next(createProposalDTO);
    }
    RefreshPartInfoids(ids: number[]) {
        this.partInfoIdsSource.next(ids);
    }
    RefreshPartInfoList(parts: PartBasicInfo[]) {
        this.partInfoListSource.next(parts);
    }
    RefreshpartInfoToBeCreatedList(parts: PartBasicInfo[]) {
        this.partInfoToBeCreatedListSource.next(parts);
    }
    RefreshpackagelevelTemplateList(
        packageLevelTemplates: PackageLevelTemplate[]
    ) {
        this.packagelevelTemplateListSource.next(packageLevelTemplates);
    }
    RefreshPackageTypeId(id: number) {
        this.packageTypeIdSource.next(id);
    }
    RefreshManufacturingLocation(location: ProposalLocation) {
      this.proposalManufacturingLocationSource.next(location);
    }

    RefreshSecondaryProcessingLocation(location: ProposalLocation) {
      this.proposalSecondaryProcessingLocationSource.next(location);
    }

    RefreshPackageTypeDescription(description: string) {
        this.packageTypeDescriptionSource.next(description);
    }
    RefreshTransportModeId(id: number) {
        this.transportModeIdSource.next(id);
    }
    RefreshTransportDescription(description: string) {
        this.transportModeDescriptionSource.next(description);
    }
    RefreshSourceTypeDescription(description: string) {
        this.sourceTypeDescriptionSource.next(description);
    }
    RefreshSourceTypeId(id: number) {
        this.sourceTypeIdSource.next(id);
    }
    RefreshPackagingMaterialId(id: number, description: string, level: number) {
        this.packagingMaterialIdSource.next(id);
        switch (level) {
            case 1: {
                this.packagingMaterialPrimaryDescriptionSource.next(
                    description
                );
                break;
            }
            case 2: {
                this.packagingMaterialSecondaryDescriptionSource.next(
                    description
                );
                break;
            }
            case 3: {
                this.packagingMaterialTertiaryDescriptionSource.next(
                    description
                );
                break;
            }
        }
    }
    RefreshPackagingTemplateDescription(description: string, level: number) {
        switch (level) {
            case 1: {
                this.packagingTemplatePrimaryDescriptionSource.next(
                    description
                );
                break;
            }
            case 2: {
                this.packagingTemplateSecondaryDescriptionSource.next(
                    description
                );
                break;
            }
            case 3: {
                this.packagingTemplateTertiaryDescriptionSource.next(
                    description
                );
                break;
            }
        }
    }

    RefreshPackagingDunnageTypeId(id: number[], description: string, level: number) {
        // this.packagingDunnageTypeIdSource.next(id);
        switch (level) {
            case 1: {
                this.packagingDunnageTypePrimaryDescriptionSource.next(
                    description
                );
                break;
            }
            case 2: {
                this.packagingDunnageTypeSecondaryDescriptionSource.next(
                    description
                );
                break;
            }
            case 3: {
                this.packagingDunnageTypeTertiaryDescriptionSource.next(
                    description
                );
                break;
            }
        }
    }
    // RefreshPackagingMaterialPrimaryId(id: number)
    // {
    //   this.packagingMaterialPrimaryIdSource.next(id);
    // }
    // RefreshPackagingMaterialSecondaryId(id: number)
    // {
    //   this.packagingMaterialSecondaryIdSource.next(id);
    // }
    // RefreshPackagingMaterialTertiaryId(id: number)
    // {
    //   this.packagingMaterialTertiaryIdSource.next(id);
    // }

    RefreshProposalTabsSelectedIndex(selectedIndex: number) {
        this.proposalTabsSelectedIndexSource.next(selectedIndex);
    }
    RefreshHighestTabIndex(index: number) {
        this.highestTabIndexSource.next(index);
    }
}
